var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/** MARC cataloguing-source field. */
export var CATALOGING_SOURCE_TAG = '040';
/** `$a` — sigla of the library that created the record. */
export var CREATING_AGENCY_SUBFIELD = 'a';
/**
 * CZNA-709 — show `040 $a` prefilled with the value the SYSTEM will store, instead of an empty
 * box to type into.
 *
 * Two NK units asked for the same thing three weeks apart ("pole 040 vygeneruje systém?",
 * Pilíková 24. 7. 2026; "zdroj katalogizace (sigla)" among the system-set values, Šťastná
 * 8. 8. 2026). The sigla follows from who is logged in, so typing it can only introduce a
 * foreign sigla, a typo, or nothing at all — and `040` is mandatory, so "nothing at all"
 * blocks the publish.
 *
 * WHY THIS IS A RUNTIME OVERLAY AND NOT A TEMPLATE EDIT. CZNA-664 deliberately left `$a`
 * without a `defaultValue`, and that decision still holds: `organization.sigla` is
 * per-organization data (ZKA001, ZKB001, …), so a constant baked into the shipped template
 * would stamp NK onto records another library created — the exact error this ticket
 * removes. What changes here is not the document but the moment: the value is known only
 * once we know WHO is looking, so it is applied to the compiled config the form is about to
 * render, and the packaged template stays a template.
 *
 * WHAT IT DOES NOT DO, AND WHY THAT IS SAFE. It is not the guarantee. The backend stamps
 * `040 $a` on every proposal write from `UserAuthorizationContext.organizationSigla`
 * (`CatalogingSourceStamper`), so a form that never applied this — no sigla on the account,
 * an API client, an older bundle — still produces a correct record. This half exists
 * because NK asked to SEE what the record is signed with, which a value stamped invisibly
 * on the server does not deliver.
 *
 * A DEFAULT, NOT A LOCK (CZNA-677). The prefilled value stays editable. CZNA-709 shipped it
 * `readOnly`; CZNA-677 reversed that, because what NK asked for is that the system FILLS the
 * sigla ("pole 040 vygeneruje systém?"), not that nobody can correct it — a library
 * cataloguing on another's behalf, or an organization record carrying the wrong sigla, has
 * no other way out on screen. The backend honours the correction: `CatalogingSourceStamper`
 * never overwrites a non-blank `$a`, on create or on a later save
 * (`ProposalServiceImplTest.CatalogingSource#anOverwrittenSiglaSurvivesTheNextSave`).
 */
export var withCatalogingSourceSigla = function (config, sigla, 
/**
 * False for an UPDATE_EXISTING proposal: `$a` there belongs to the record being amended
 * — the founding library, which is usually not the caller — and the amending library is
 * recorded as `$d` by the backend instead. Prefilling the caller's sigla into `$a` would
 * rewrite the founder on any record that arrived without one, and 1.27 M imported
 * records are in exactly that position.
 */
applies) {
    var _a;
    var trimmed = sigla === null || sigla === void 0 ? void 0 : sigla.trim();
    if (!config || !trimmed || !applies)
        return config;
    var changed = false;
    var tabs = ((_a = config.tabs) !== null && _a !== void 0 ? _a : []).map(function (tab) {
        var _a;
        if (!((_a = tab.fields) === null || _a === void 0 ? void 0 : _a.some(function (fd) { return fd.tag === CATALOGING_SOURCE_TAG; })))
            return tab;
        return __assign(__assign({}, tab), { fields: tab.fields.map(function (fd) {
                var _a;
                if (fd.tag !== CATALOGING_SOURCE_TAG)
                    return fd;
                if (!((_a = fd.subfields) === null || _a === void 0 ? void 0 : _a.some(function (sf) { return sf.code === CREATING_AGENCY_SUBFIELD; })))
                    return fd;
                changed = true;
                return __assign(__assign({}, fd), { subfields: fd.subfields.map(function (sf) { return (sf.code === CREATING_AGENCY_SUBFIELD
                        ? __assign(__assign({}, sf), { defaultValue: trimmed }) : sf); }) });
            }) });
    });
    // Identity when there was nothing to overlay — a new object on every render would
    // remount the whole config-driven form.
    return changed ? __assign(__assign({}, config), { tabs: tabs }) : config;
};
/**
 * Is this form FOUNDING a record — the only situation in which the caller's sigla is the
 * right answer for `040 $a`?
 *
 * Three conditions, and each one excludes a way of being wrong rather than restating the
 * others:
 *
 * - **`mode !== 'edit'`.** Reopening an existing draft is not founding it. Without this the
 *   overlay prefills the reader's sigla into any draft that carries no stored `$a`, which is
 *   every draft that existed before this shipped and every draft from an account with no
 *   sigla. An NK administrator opening an Olomouc draft would then save it signed ABA001
 *   unless they noticed and cleared it — the value is editable since CZNA-677, but a prefill
 *   nobody asked for is exactly the error this exists to remove, reintroduced through the
 *   fix.
 * - **no `targetRecordAuthorityId`.** An amendment always carries the target's id, so this
 *   catches an amendment-creating route even if it forgets to pass `proposalType`.
 * - **`proposalType !== UPDATE_EXISTING`.** The explicit statement of the same thing, for
 *   the routes that do pass it.
 *
 * The last two are deliberately redundant. `proposalType` is optional and the create route
 * passes none, so `!== UPDATE_EXISTING` alone reads `undefined` as "founding" — which is
 * true today and would silently stop being true the day an amendment-create route appears.
 */
export var isFoundingForm = function (form) { return form.mode !== 'edit'
    && !form.targetRecordAuthorityId
    && form.proposalType !== 'UPDATE_EXISTING'; };
export default withCatalogingSourceSigla;
